import { Button } from '@suscri/ui';
import {
  Plus,
  Search,
  Filter,
  X,
  Edit,
  Trash2,
  Upload,
  CreditCard,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportSubscriptionsModal } from '../components/ImportSubscriptionsModal';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuthenticatedQuery } from '../hooks/useAuthenticatedQuery';
import { useToast } from '../lib/toast';
import { trpc } from '../lib/trpc';

// Vista mínima de los campos que usa esta página. El tipo inferido completo
// de subscriptions.list dispara TS2589 (instanciación excesivamente profunda)
// por los tipos Decimal de Prisma al atravesar la serialización tRPC.
type SubscriptionListItem = {
  id: string;
  name: string;
  amount: string | number;
  currency: string;
  cycle: 'MONTHLY' | 'YEARLY' | 'CUSTOM';
  status: string;
  startDate: string | Date;
  nextRenewal: string | Date;
  providerId: string | null;
  categoryId: string | null;
  notes: string | null;
  provider: { name: string } | null;
};

export function SubscriptionsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionListItem | null>(null);
  const [filters] = useState({
    status: '',
    providerId: '',
    categoryId: '',
  });

  // Formulario de nueva suscripción
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    currency: 'EUR',
    cycle: 'MONTHLY' as 'MONTHLY' | 'YEARLY' | 'CUSTOM',
    startDate: new Date().toISOString().split('T')[0],
    providerId: '',
    categoryId: '',
    notes: '',
  });

  const { enabled } = useAuthenticatedQuery();
  const toast = useToast();
  const { data, isLoading, refetch } = trpc.subscriptions.list.useQuery(
    {
      filters: {
        search: search || undefined,
        status: filters.status || undefined,
        providerId: filters.providerId || undefined,
        categoryId: filters.categoryId || undefined,
      },
      pagination: { page: 1, limit: 20 },
    },
    {
      enabled,
    }
  );

  // Obtener proveedores y categorías para el formulario
  const { data: providers } = trpc.providers.list.useQuery(
    {
      pagination: { page: 1, limit: 100 },
    },
    {
      enabled,
    }
  );

  const { data: categories } = trpc.categories.list.useQuery(
    {
      pagination: { page: 1, limit: 100 },
    },
    {
      enabled,
    }
  );

  // Mutación para crear suscripción
  const createSubscription = trpc.subscriptions.create.useMutation({
    onSuccess: () => {
      setShowCreateModal(false);
      setFormData({
        name: '',
        amount: '',
        currency: 'EUR',
        cycle: 'MONTHLY',
        startDate: new Date().toISOString().split('T')[0],
        providerId: '',
        categoryId: '',
        notes: '',
      });
      refetch();
    },
    onError: () => {
      toast.error(t('No se pudo crear la suscripción. Inténtalo de nuevo.'));
    },
  });

  // Mutación para actualizar suscripción
  const updateSubscription = trpc.subscriptions.update.useMutation({
    onSuccess: () => {
      setShowEditModal(false);
      setSelectedSubscription(null);
      refetch();
    },
    onError: () => {
      toast.error(
        t('No se pudo actualizar la suscripción. Inténtalo de nuevo.')
      );
    },
  });

  // Mutación para eliminar suscripción
  const deleteSubscription = trpc.subscriptions.delete.useMutation({
    onSuccess: () => {
      setShowDeleteModal(false);
      setSelectedSubscription(null);
      refetch();
    },
    onError: () => {
      toast.error(t('No se pudo eliminar la suscripción. Inténtalo de nuevo.'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.amount) return;

    const startDate = new Date(formData.startDate);
    const nextRenewal = new Date(startDate);

    if (formData.cycle === 'MONTHLY') {
      nextRenewal.setMonth(nextRenewal.getMonth() + 1);
    } else if (formData.cycle === 'YEARLY') {
      nextRenewal.setFullYear(nextRenewal.getFullYear() + 1);
    } else if (formData.cycle === 'CUSTOM') {
      // Para ciclos personalizados, usar 1 mes por defecto
      nextRenewal.setMonth(nextRenewal.getMonth() + 1);
    }

    createSubscription.mutate({
      name: formData.name,
      amount: parseFloat(formData.amount),
      currency: formData.currency,
      cycle: formData.cycle,
      startDate,
      nextRenewal,
      providerId: formData.providerId || undefined,
      categoryId: formData.categoryId || undefined,
      notes: formData.notes,
    });
  };

  const handleEdit = (subscription: SubscriptionListItem) => {
    setSelectedSubscription(subscription);
    setFormData({
      name: subscription.name,
      amount: subscription.amount.toString(),
      currency: subscription.currency,
      cycle: subscription.cycle,
      startDate: new Date(subscription.startDate).toISOString().split('T')[0],
      providerId: subscription.providerId || '',
      categoryId: subscription.categoryId || '',
      notes: subscription.notes || '',
    });
    setShowEditModal(true);
  };

  const handleDelete = (subscription: SubscriptionListItem) => {
    setSelectedSubscription(subscription);
    setShowDeleteModal(true);
  };

  const handleUpdateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.amount || !selectedSubscription) return;

    const startDate = new Date(formData.startDate);
    const nextRenewal = new Date(startDate);

    if (formData.cycle === 'MONTHLY') {
      nextRenewal.setMonth(nextRenewal.getMonth() + 1);
    } else if (formData.cycle === 'YEARLY') {
      nextRenewal.setFullYear(nextRenewal.getFullYear() + 1);
    } else if (formData.cycle === 'CUSTOM') {
      nextRenewal.setMonth(nextRenewal.getMonth() + 1);
    }

    updateSubscription.mutate({
      id: selectedSubscription.id,
      data: {
        name: formData.name,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        cycle: formData.cycle,
        startDate,
        nextRenewal,
        providerId: formData.providerId || undefined,
        categoryId: formData.categoryId || undefined,
        notes: formData.notes,
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (selectedSubscription) {
      deleteSubscription.mutate(selectedSubscription.id);
    }
  };

  const openCreateModal = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedSubscription(null);
    setFormData({
      name: '',
      amount: '',
      currency: 'EUR',
      cycle: 'MONTHLY',
      startDate: new Date().toISOString().split('T')[0],
      providerId: '',
      categoryId: '',
      notes: '',
    });
    setShowCreateModal(true);
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('Gestión')}
        title={t('Suscripciones')}
        description={t('Gestiona todas tus suscripciones')}
        actions={
          <>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <Upload className="h-4 w-4" />
            {t('Importar')}
          </Button>
          <Button onClick={openCreateModal}>
            <Plus className="h-4 w-4" />
            {t('Nueva Suscripción')}
          </Button>
          </>
        }
      />

      <div className="surface-card mb-6 flex items-center gap-2 p-2.5 sm:p-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('Buscar suscripciones...')}
            className="control w-full border-transparent bg-background/50 pl-10 pr-4"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Button variant="ghost" className="shrink-0">
          <Filter className="h-4 w-4" />
          <span className="hidden sm:inline">{t('Filtros')}</span>
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl border border-border bg-muted/70" />
          ))}
        </div>
      ) : data?.data.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title={t('Todavía no tienes suscripciones')}
          description={t('Añade tu primera suscripción a mano o importa varias de golpe desde un CSV/Excel.')}
          primaryAction={{ label: t('Añadir manualmente'), onClick: openCreateModal }}
          secondaryAction={
            <Button variant="outline" onClick={() => setShowImportModal(true)}>
              <Upload className="h-4 w-4" />
              {t('Importar desde CSV/Excel')}
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {(data?.data as unknown as SubscriptionListItem[] | undefined)?.map(
            subscription => (
              <div
                key={subscription.id}
                onClick={() => handleEdit(subscription)}
                onKeyDown={event => {
                  if (event.key === 'Enter') handleEdit(subscription);
                }}
                role="button"
                tabIndex={0}
                className="surface-card-interactive group relative cursor-pointer overflow-hidden p-4 sm:p-5"
              >
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary/[0.06] to-secondary/[0.03] opacity-0 transition group-hover:opacity-100" />
                <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3.5">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-gradient-to-br from-primary/25 to-secondary/15 shadow-sm">
                      <span className="text-sm font-bold text-primary">
                        {subscription.name.charAt(0)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold text-foreground sm:text-lg">
                        {subscription.name}
                      </h3>
                      <p className="truncate text-sm text-muted-foreground">
                        {subscription.provider?.name || t('Sin proveedor')}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 border-t border-border/70 pt-4 sm:flex-nowrap sm:gap-6 sm:border-0 sm:pt-0">
                    <div className="min-w-24 sm:text-right">
                      <p className="text-xl font-bold text-foreground sm:text-2xl">
                        €{subscription.amount}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {subscription.cycle === 'MONTHLY'
                          ? t('Mensual')
                          : subscription.cycle === 'YEARLY'
                            ? t('Anual')
                            : t('Personalizado')}
                      </p>
                    </div>

                    <div className="min-w-28 sm:text-right">
                      <span
                        className={`status-badge ${
                          subscription.status === 'active'
                            ? 'border-success/20 bg-success/10 text-success'
                            : subscription.status === 'paused'
                              ? 'border-warning/20 bg-warning/10 text-warning'
                              : 'border-danger/20 bg-danger/10 text-danger'
                        }`}
                      >
                        {subscription.status === 'active'
                          ? t('Activa')
                          : subscription.status === 'paused'
                            ? t('Pausada')
                            : t('Cancelada')}
                      </span>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {t('Próxima:')}{' '}
                        {new Date(
                          subscription.nextRenewal
                        ).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="ml-auto flex gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={event => {
                          event.stopPropagation();
                          handleEdit(subscription);
                        }}
                        aria-label={t('Editar Suscripción')}
                        className="text-muted-foreground hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={event => {
                          event.stopPropagation();
                          handleDelete(subscription);
                        }}
                        aria-label={t('Eliminar Suscripción')}
                        className="text-danger hover:border-danger/30 hover:bg-danger/10 hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Modal de Nueva Suscripción */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="create-subscription-title">
          <div className="scrollbar-subtle max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-popover p-5 text-popover-foreground shadow-2xl sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 id="create-subscription-title" className="text-lg font-semibold text-foreground">
                {t('Nueva Suscripción')}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateModal(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('Cerrar')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('Nombre de la suscripción *')}
                </label>
                <input
                  type="text"
                  placeholder="Ej: Spotify Premium"
                  value={formData.name}
                  onChange={e =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="control w-full"
                  required
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Precio *')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="9.99"
                    value={formData.amount}
                    onChange={e =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    className="control w-full"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Moneda')}
                  </label>
                  <select
                    value={formData.currency}
                    onChange={e =>
                      setFormData({ ...formData, currency: e.target.value })
                    }
                    className="control w-full"
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('Ciclo de facturación')}
                </label>
                <select
                  value={formData.cycle}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      cycle: e.target.value as 'MONTHLY' | 'YEARLY' | 'CUSTOM',
                    })
                  }
                  className="control w-full"
                >
                  <option value="MONTHLY">{t('Mensual')}</option>
                  <option value="YEARLY">{t('Anual')}</option>
                  <option value="CUSTOM">{t('Personalizado')}</option>
                </select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Proveedor (opcional)')}
                  </label>
                  <select
                    value={formData.providerId}
                    onChange={e =>
                      setFormData({ ...formData, providerId: e.target.value })
                    }
                    className="control w-full"
                  >
                    <option value="">{t('Seleccionar proveedor')}</option>
                    {providers?.data.map(provider => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Categoría (opcional)')}
                  </label>
                  <select
                    value={formData.categoryId}
                    onChange={e =>
                      setFormData({ ...formData, categoryId: e.target.value })
                    }
                    className="control w-full"
                  >
                    <option value="">{t('Seleccionar categoría')}</option>
                    {categories?.data.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('Fecha de inicio')}
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                  className="control w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('Notas (opcional)')}
                </label>
                <textarea
                  placeholder={t(
                    'Información adicional sobre la suscripción...'
                  )}
                  value={formData.notes}
                  onChange={e =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  className="control w-full py-3"
                  rows={3}
                />
              </div>

              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCreateModal(false)}
                >
                  {t('Cancelar')}
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={createSubscription.isPending}
                >
                  {createSubscription.isPending
                    ? t('Creando...')
                    : t('Crear Suscripción')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Edición de Suscripción */}
      {showEditModal && selectedSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="edit-subscription-title">
          <div className="scrollbar-subtle max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-popover p-5 text-popover-foreground shadow-2xl sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 id="edit-subscription-title" className="text-lg font-semibold text-foreground">
                {t('Editar Suscripción')}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('Cerrar')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleUpdateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('Nombre de la suscripción *')}
                </label>
                <input
                  type="text"
                  placeholder="Ej: Spotify Premium"
                  value={formData.name}
                  onChange={e =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="control w-full"
                  required
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Precio *')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="9.99"
                    value={formData.amount}
                    onChange={e =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    className="control w-full"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Moneda')}
                  </label>
                  <select
                    value={formData.currency}
                    onChange={e =>
                      setFormData({ ...formData, currency: e.target.value })
                    }
                    className="control w-full"
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('Ciclo de facturación')}
                </label>
                <select
                  value={formData.cycle}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      cycle: e.target.value as 'MONTHLY' | 'YEARLY' | 'CUSTOM',
                    })
                  }
                  className="control w-full"
                >
                  <option value="MONTHLY">{t('Mensual')}</option>
                  <option value="YEARLY">{t('Anual')}</option>
                  <option value="CUSTOM">{t('Personalizado')}</option>
                </select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Proveedor (opcional)')}
                  </label>
                  <select
                    value={formData.providerId}
                    onChange={e =>
                      setFormData({ ...formData, providerId: e.target.value })
                    }
                    className="control w-full"
                  >
                    <option value="">{t('Seleccionar proveedor')}</option>
                    {providers?.data.map(provider => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Categoría (opcional)')}
                  </label>
                  <select
                    value={formData.categoryId}
                    onChange={e =>
                      setFormData({ ...formData, categoryId: e.target.value })
                    }
                    className="control w-full"
                  >
                    <option value="">{t('Seleccionar categoría')}</option>
                    {categories?.data.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('Fecha de inicio')}
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                  className="control w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('Notas (opcional)')}
                </label>
                <textarea
                  placeholder={t(
                    'Información adicional sobre la suscripción...'
                  )}
                  value={formData.notes}
                  onChange={e =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  className="control w-full py-3"
                  rows={3}
                />
              </div>

              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowEditModal(false)}
                >
                  {t('Cancelar')}
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={updateSubscription.isPending}
                >
                  {updateSubscription.isPending
                    ? t('Actualizando...')
                    : t('Actualizar Suscripción')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {showDeleteModal && selectedSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="delete-subscription-title">
          <div className="w-full max-w-md rounded-2xl border border-border bg-popover p-5 text-popover-foreground shadow-2xl sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 id="delete-subscription-title" className="text-lg font-semibold text-foreground">
                {t('Eliminar Suscripción')}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteModal(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('Cerrar')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <p className="text-sm leading-6 text-muted-foreground">
                {t('¿Estás seguro de que quieres eliminar la suscripción')}{' '}
                <strong>"{selectedSubscription.name}"</strong>
                {t('? Esta acción no se puede deshacer.')}
              </p>

              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDeleteModal(false)}
                >
                  {t('Cancelar')}
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleDeleteConfirm}
                  disabled={deleteSubscription.isPending}
                >
                  {deleteSubscription.isPending
                    ? t('Eliminando...')
                    : t('Eliminar')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <ImportSubscriptionsModal
          onClose={() => setShowImportModal(false)}
          onImported={() => refetch()}
        />
      )}
    </div>
  );
}
